import request from '../../../util/SuperagentUtils';
import {
  buyerBookingsReportEndpoints,
  supplierBookingsReportEndpoints,
} from '../../../requests/api-endpoints';

/** The server's page-size ceiling (`PageableParams.MAX_PAGE_SIZE`). */
export const ACTIVE_BOOKINGS_PAGE_SIZE = 100;

/**
 * Most pages fetched to group active bookings by location. A supplier with more
 * active bookings than this covers gets a partial split, which the skill says
 * out loud, rather than a burst of requests to render one chat answer.
 */
export const MAX_ACTIVE_BOOKING_PAGES = 10;

/** One active booking. Loose and local — only the fields the skills read. */
export interface ActiveBookingRow {
  locationId?: string;
  locationName?: string;
  orderNumber?: string;
  /** `MM/dd/yyyy`. Month-to-month bookings run to a far-future placeholder. */
  startDate?: string;
  /** `MM/dd/yyyy`. Month-to-month bookings run to a far-future placeholder. */
  endDate?: string;
  /** Supplier rows only. */
  buyerCompanyName?: string;
  /** Buyer rows only. */
  supplierCompanyName?: string;
  numberOfSpaces?: number;
  durationType?: string;
}

/** Account types with a bookings search of their own. */
export type ActiveBookingsAudience = 'supplier' | 'buyer';

const SEARCH_ENDPOINT: Record<ActiveBookingsAudience, string> = {
  supplier: supplierBookingsReportEndpoints.search,
  buyer: buyerBookingsReportEndpoints.search,
};

interface ActiveBookingsPage {
  content?: ActiveBookingRow[];
  count?: number;
  totalPages?: number;
}

export interface ActiveBookingsSample {
  rows: ActiveBookingRow[];
  /** Every active booking on the account, which may exceed `rows.length`. */
  total: number;
}

/**
 * One page of the signed-in account's active bookings.
 *
 * Sorted by order number because it is unique: the default start-date sort has
 * ties, and offset pages over a tied sort can repeat or skip rows — which would
 * miscount a location when the pages are stitched back together.
 */
const requestActiveBookingsPage = (
  audience: ActiveBookingsAudience,
  page: number,
): Promise<{ body?: ActiveBookingsPage }> =>
  request.get(SEARCH_ENDPOINT[audience]).query({
    activeStatus: 'ACTIVE',
    sortBy: 'ORDER_NUMBER',
    sortDir: 'asc',
    page,
    size: ACTIVE_BOOKINGS_PAGE_SIZE,
  });

/**
 * Fetches the signed-in account's active bookings, for answers the search
 * cannot give on its own.
 *
 * The search endpoint only counts per location or per account, and can neither
 * filter nor sort on end date — so "which of my locations have bookings" would
 * cost one request per location, and "which bookings end this month" has no
 * request at all. Pulling the rows instead is a single request for most
 * accounts, with later pages fetched in parallel up to
 * {@link MAX_ACTIVE_BOOKING_PAGES}.
 */
export const fetchActiveBookings = async (
  audience: ActiveBookingsAudience,
): Promise<ActiveBookingsSample> => {
  const first = await requestActiveBookingsPage(audience, 0);
  const total = first?.body?.count ?? 0;
  const pageCount = Math.min(first?.body?.totalPages ?? 1, MAX_ACTIVE_BOOKING_PAGES);

  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) =>
      requestActiveBookingsPage(audience, index + 1),
    ),
  );

  const rows = [first, ...rest].flatMap((response) => response?.body?.content ?? []);
  return { rows, total: Math.max(total, rows.length) };
};

/** The supplier's active bookings, to group by location. */
export const fetchActiveSupplierBookings = (): Promise<ActiveBookingsSample> =>
  fetchActiveBookings('supplier');
